// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '../../types';
import type { ContactSubmission } from '../../types/admin';
import { format } from 'date-fns';
import { AdminSearchBar } from './AdminSearchBar';
import { AdminPagination } from './AdminPagination';
import { AdminConfirmDialog } from './AdminConfirmDialog';
import { AdminQuickFilters } from './AdminQuickFilters';
import { AdminCopyButton } from './AdminCopyButton';
import { AdminLoadingSkeleton } from './AdminLoadingSkeleton';
import { AdminDateRangeFilter } from './AdminDateRangeFilter';
import { useAdminToast } from './useAdminToast';
import { useDebounce } from './useDebounce';
import { exportToCSV, exportToJSON } from './AdminExportUtils';
import Papa from 'papaparse';

interface AdminContactsProps {
  session: Session;
  profile: Profile | null;
}

export const AdminContacts: React.FC<AdminContactsProps> = ({ session, profile }) => {
  const [contacts, setContacts] = useState<ContactSubmission[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState<ContactSubmission | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'read' | 'replied' | 'archived'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [assigningContact, setAssigningContact] = useState<string | null>(null);
  const { showSuccess, showError } = useAdminToast();
  
  // Debounce search input
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    loadContacts();

    // Set up real-time subscription
    const channel = supabase
      .channel('admin-contacts-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contact_submissions',
        },
        () => {
          loadContacts();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let filtered = contacts;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((contact) => contact.status === statusFilter);
    }

    // Apply date range filter
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter((contact) => {
        const contactDate = new Date(contact.created_at);
        return contactDate >= dateRange.start! && contactDate <= dateRange.end!;
      });
    }

    // Apply search filter (using debounced value)
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (contact) =>
          contact.name.toLowerCase().includes(query) ||
          contact.email.toLowerCase().includes(query) ||
          contact.message.toLowerCase().includes(query)
      );
    }

    setFilteredContacts(filtered);
  }, [contacts, debouncedSearchQuery, statusFilter, dateRange]);

  // Update quick filters to use statusFilter for active state
  const activeQuickFilterId = statusFilter === 'all' ? undefined : statusFilter;

  const quickFilters = [
    {
      id: 'all',
      label: 'All',
      icon: 'mail',
      onClick: () => setStatusFilter('all'),
      count: contacts.length,
    },
    {
      id: 'new',
      label: 'New',
      icon: 'schedule',
      onClick: () => setStatusFilter('new'),
      count: contacts.filter((c) => c.status === 'new').length,
    },
    {
      id: 'read',
      label: 'Read',
      icon: 'visibility',
      onClick: () => setStatusFilter('read'),
      count: contacts.filter((c) => c.status === 'read').length,
    },
    {
      id: 'replied',
      label: 'Replied',
      icon: 'reply',
      onClick: () => setStatusFilter('replied'),
      count: contacts.filter((c) => c.status === 'replied').length,
    },
    {
      id: 'archived',
      label: 'Archived',
      icon: 'archive',
      onClick: () => setStatusFilter('archived'),
      count: contacts.filter((c) => c.status === 'archived').length,
    },
  ];

  const handleExportCSV = () => {
    const dataToExport = selectedRows.length > 0 
      ? filteredContacts.filter((c) => selectedRows.includes(c.id))
      : filteredContacts;
    
    const csvData = dataToExport.map((contact) => ({
      Name: contact.name,
      Email: contact.email,
      Message: contact.message,
      Status: contact.status,
      'Admin Notes': contact.admin_notes || '',
      'Replied At': contact.replied_at ? format(new Date(contact.replied_at), 'yyyy-MM-dd HH:mm:ss') : 'N/A',
      'Created At': format(new Date(contact.created_at), 'yyyy-MM-dd HH:mm:ss'),
    }));
    
    exportToCSV(csvData, selectedRows.length > 0 ? 'selected_contacts' : 'contacts');
    showSuccess(
      selectedRows.length > 0
        ? `${selectedRows.length} selected contact(s) exported to CSV successfully`
        : 'Contacts exported to CSV successfully'
    );
  };

  const handleExportJSON = () => {
    const dataToExport = selectedRows.length > 0 
      ? filteredContacts.filter((c) => selectedRows.includes(c.id))
      : filteredContacts;
    
    exportToJSON(dataToExport, selectedRows.length > 0 ? 'selected_contacts' : 'contacts');
    showSuccess(
      selectedRows.length > 0
        ? `${selectedRows.length} selected contact(s) exported to JSON successfully`
        : 'Contacts exported to JSON successfully'
    );
  };

  const loadContacts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (error.message?.includes('does not exist') || error.code === '42P01') {
          setContacts([]);
          return;
        }
        throw error;
      }
      setContacts(data || []);
    } catch (err) {
      console.error('Error loading contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateContactStatus = async (id: string, status: ContactSubmission['status']) => {
    try {
      setUpdatingStatus(id);
      
      const updateData: any = { 
        status
      };
      
      // Set replied_at and handled_at when status is replied
      if (status === 'replied') {
        updateData.replied_at = new Date().toISOString();
        updateData.handled_at = new Date().toISOString();
        // Auto-assign to current admin when replying
        updateData.assigned_to = profile?.id;
      }
      // Set handled_at when status is archived or read
      else if (status === 'archived' || status === 'read') {
        updateData.handled_at = new Date().toISOString();
        // Auto-assign to current admin when marking as read or archived
        updateData.assigned_to = profile?.id;
      }

      const { error } = await supabase
        .from('contact_submissions')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      // Update local state
      setContacts(prev => 
        prev.map(contact => 
          contact.id === id 
            ? { 
                ...contact, 
                ...updateData
              } 
            : contact
        )
      );

      showSuccess('Contact status updated successfully');
    } catch (err) {
      console.error('Error updating contact status:', err);
      showError('Failed to update contact status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const deleteContact = async (id: string) => {
    try {
      const { error } = await supabase
        .from('contact_submissions')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update local state
      setContacts(prev => prev.filter(contact => contact.id !== id));
      setSelectedRows(prev => prev.filter(rowId => rowId !== id));
      
      showSuccess('Contact deleted successfully');
    } catch (err) {
      console.error('Error deleting contact:', err);
      showError('Failed to delete contact');
    }
  };

  const bulkDeleteContacts = async (ids: string[]) => {
    try {
      const { error } = await supabase
        .from('contact_submissions')
        .delete()
        .in('id', ids);

      if (error) throw error;

      // Update local state
      setContacts(prev => prev.filter(contact => !ids.includes(contact.id)));
      setSelectedRows([]);

      showSuccess(`${ids.length} contact(s) deleted successfully`);
    } catch (err) {
      console.error('Error deleting contacts:', err);
      showError('Failed to delete contacts');
    }
  };

  const handleSelectRow = (id: string) => {
    setSelectedRows(prev => 
      prev.includes(id) 
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === filteredContacts.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredContacts.map(contact => contact.id));
    }
  };

  const confirmDelete = async () => {
    if (deleteTarget === 'bulk') {
      await bulkDeleteContacts(selectedRows);
    } else if (deleteTarget) {
      await deleteContact(deleteTarget);
    }
    setShowDeleteDialog(false);
    setDeleteTarget(null);
  };

  const assignContactToAdmin = async (id: string, adminId: string | null) => {
    try {
      setAssigningContact(id);
      
      const updateData: any = { 
        assigned_to: adminId
      };
      
      // If assigning to an admin and contact is new, mark as read
      const contact = contacts.find(c => c.id === id);
      if (adminId && contact && contact.status === 'new') {
        updateData.status = 'read';
        updateData.handled_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('contact_submissions')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      // Update local state
      setContacts(prev => 
        prev.map(contact => 
          contact.id === id 
            ? { 
                ...contact, 
                ...updateData
              } 
            : contact
        )
      );

      showSuccess(adminId ? 'Contact assigned successfully' : 'Contact unassigned successfully');
    } catch (err) {
      console.error('Error assigning contact:', err);
      showError('Failed to assign contact');
    } finally {
      setAssigningContact(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="mb-6">
          <h1 className="text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
            Contact Submissions
          </h1>
          <p className="text-luna-text-light/70 dark:text-luna-text-dark/70">
            Manage contact form submissions
          </p>
        </div>
        <AdminLoadingSkeleton count={5} />
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="space-y-4">
        <div className="mb-6">
          <h1 className="text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
            Contact Submissions
          </h1>
          <p className="text-luna-text-light/70 dark:text-luna-text-dark/70">
            Manage contact form submissions
          </p>
        </div>
        <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-12 text-center">
          <p className="text-luna-text-light/70 dark:text-luna-text-dark/70">
            No contact submissions found. The contact_submissions table may not exist.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
              Contact Submissions
            </h1>
            <p className="text-luna-text-light/70 dark:text-luna-text-dark/70">
              Manage contact form submissions ({filteredContacts.length} of {contacts.length})
            </p>
          </div>
              <div className="flex gap-2">
                {selectedRows.length > 0 && (
                  <button
                    onClick={() => {
                      setDeleteTarget('bulk');
                      setShowDeleteDialog(true);
                    }}
                    className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors flex items-center gap-1"
                  >
                    <span className="material-symbols-outlined text-base">delete</span>
                    Delete Selected ({selectedRows.length})
                  </button>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={handleExportCSV}
                    className="px-3 py-1.5 rounded-lg bg-luna-secondary text-luna-primary text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-1"
                  >
                    <span className="material-symbols-outlined text-base">download</span>
                    Export CSV
                  </button>
                  <button
                    onClick={handleExportJSON}
                    className="px-3 py-1.5 rounded-lg bg-luna-secondary text-luna-primary text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-1"
                  >
                    <span className="material-symbols-outlined text-base">download</span>
                    Export JSON
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <AdminSearchBar 
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search contacts..."
              />
              <AdminDateRangeFilter 
                value={dateRange}
                onChange={setDateRange}
              />
            </div>
          </div>
          
          <AdminQuickFilters 
            filters={quickFilters}
            activeFilterId={activeQuickFilterId}
          />
          
          {selectedRows.length > 0 && (
            <div className="bg-luna-secondary/10 border border-luna-secondary/30 rounded-lg p-3 flex items-center justify-between">
              <span className="text-sm font-medium text-luna-secondary">
                {selectedRows.length} row(s) selected
              </span>
              <div className="flex gap-2">
                <select
                  onChange={(e) => {
                    const newStatus = e.target.value as ContactSubmission['status'];
                    selectedRows.forEach(id => {
                      updateContactStatus(id, newStatus);
                    });
                    setSelectedRows([]);
                  }}
                  className="px-2 py-1 rounded border border-luna-text-light/20 dark:border-luna-text-dark/20 bg-white dark:bg-luna-primary text-sm"
                >
                  <option value="">Update Status</option>
                  <option value="new">New</option>
                  <option value="read">Read</option>
                  <option value="replied">Replied</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
          )}
          
          <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-luna-text-light/10 dark:divide-luna-text-dark/20">
                <thead className="bg-luna-bg-light dark:bg-luna-bg-dark">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-luna-text-light/70 dark:text-luna-text-dark/70 uppercase tracking-wider w-12">
                      <input
                        type="checkbox"
                        checked={selectedRows.length === filteredContacts.length && filteredContacts.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-luna-text-light/20 dark:border-luna-text-dark/20 text-luna-secondary focus:ring-luna-secondary"
                      />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-luna-text-light/70 dark:text-luna-text-dark/70 uppercase tracking-wider">
                      Contact
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-luna-text-light/70 dark:text-luna-text-dark/70 uppercase tracking-wider">
                      Message
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-luna-text-light/70 dark:text-luna-text-dark/70 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-luna-text-light/70 dark:text-luna-text-dark/70 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-luna-text-light/70 dark:text-luna-text-dark/70 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-luna-primary divide-y divide-luna-text-light/10 dark:divide-luna-text-dark/20">
                  {filteredContacts.map((contact) => (
                    <tr 
                      key={contact.id} 
                      className={`hover:bg-luna-bg-light/50 dark:hover:bg-luna-bg-dark/50 ${selectedRows.includes(contact.id) ? 'bg-luna-secondary/5' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(contact.id)}
                          onChange={() => handleSelectRow(contact.id)}
                          className="rounded border-luna-text-light/20 dark:border-luna-text-dark/20 text-luna-secondary focus:ring-luna-secondary"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark">
                          {contact.name}
                        </div>
                        <div className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                          <a href={`mailto:${contact.email}`} className="hover:text-luna-secondary">
                            {contact.email}
                          </a>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-luna-text-light dark:text-luna-text-dark line-clamp-2">
                          {contact.message}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          contact.status === 'new' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200' :
                          contact.status === 'read' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200' :
                          contact.status === 'replied' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                        {format(new Date(contact.created_at), 'MMM d, yyyy HH:mm')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setSelectedContact(contact)}
                            className="text-luna-secondary hover:text-luna-secondary/80"
                          >
                            <span className="material-symbols-outlined">visibility</span>
                          </button>
                          <AdminCopyButton 
                            text={`${contact.name}\n${contact.email}\n\n${contact.message}`}
                            tooltip="Copy contact details"
                          />
                          <button
                            onClick={() => {
                              setDeleteTarget(contact.id);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-500 hover:text-red-600"
                          >
                            <span className="material-symbols-outlined">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <AdminPagination
              currentPage={currentPage}
              totalPages={Math.ceil(filteredContacts.length / pageSize)}
              onPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalItems={filteredContacts.length}
            />
          </div>
          
          {/* Contact Detail Modal */}
          {selectedContact && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedContact(null)}>
              <div className="bg-white dark:bg-luna-primary rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">Contact Details</h3>
                  <button onClick={() => setSelectedContact(null)} className="text-luna-text-light/60 dark:text-luna-text-dark/60">
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">Name</p>
                    <p className="text-luna-text-light dark:text-luna-text-dark">{selectedContact.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">Email</p>
                    <a 
                      href={`mailto:${selectedContact.email}?subject=Re: Your inquiry`} 
                      className="text-luna-secondary hover:underline"
                    >
                      {selectedContact.email}
                    </a>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">Message</p>
                    <div className="mt-2 p-4 bg-luna-bg-light/50 dark:bg-luna-bg-dark/50 rounded-lg">
                      <p className="text-luna-text-light dark:text-luna-text-dark whitespace-pre-wrap">
                        {selectedContact.message}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">Status</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        selectedContact.status === 'new' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200' :
                        selectedContact.status === 'read' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200' :
                        selectedContact.status === 'replied' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {selectedContact.status.charAt(0).toUpperCase() + selectedContact.status.slice(1)}
                      </span>
                      <select
                        value={selectedContact.status}
                        onChange={(e) => updateContactStatus(selectedContact.id, e.target.value as ContactSubmission['status'])}
                        disabled={!!updatingStatus}
                        className="text-xs rounded border border-luna-text-light/20 dark:border-luna-text-dark/20 bg-white dark:bg-luna-primary"
                      >
                        <option value="new">New</option>
                        <option value="read">Read</option>
                        <option value="replied">Replied</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">Assigned To</p>
                    <div className="flex items-center gap-2 mt-1">
                      {selectedContact.assigned_to ? (
                        <span className="text-sm text-luna-text-light dark:text-luna-text-dark">
                          {selectedContact.assigned_to === profile?.id ? 'You' : 'Another Admin'}
                        </span>
                      ) : (
                        <span className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">Unassigned</span>
                      )}
                      {selectedContact.assigned_to === profile?.id ? (
                        <button
                          onClick={() => assignContactToAdmin(selectedContact.id, null)}
                          className="text-xs text-red-500 hover:text-red-700"
                          disabled={!!assigningContact}
                        >
                          Unassign
                        </button>
                      ) : !selectedContact.assigned_to ? (
                        <button
                          onClick={() => assignContactToAdmin(selectedContact.id, profile?.id || null)}
                          className="text-xs text-luna-secondary hover:text-luna-secondary/80"
                          disabled={!!assigningContact}
                        >
                          Assign to Me
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">Submitted</p>
                    <p className="text-luna-text-light dark:text-luna-text-dark">
                      {format(new Date(selectedContact.created_at), 'MMMM d, yyyy h:mm a')}
                    </p>
                  </div>
                  {selectedContact.handled_at && (
                    <div>
                      <p className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">Handled At</p>
                      <p className="text-luna-text-light dark:text-luna-text-dark">
                        {format(new Date(selectedContact.handled_at), 'MMMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  )}
                  {selectedContact.admin_notes && (
                    <div>
                      <p className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">Admin Notes</p>
                      <p className="text-luna-text-light dark:text-luna-text-dark">{selectedContact.admin_notes}</p>
                    </div>
                  )}
                </div>
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    onClick={() => setSelectedContact(null)}
                    className="px-4 py-2 rounded-lg border border-luna-text-light/20 dark:border-luna-text-dark/20 text-luna-text-light dark:text-luna-text-dark text-sm font-medium hover:bg-luna-bg-light/50 dark:hover:bg-luna-bg-dark/50 transition-colors"
                  >
                    Close
                  </button>
                  <a
                    href={`mailto:${selectedContact.email}?subject=Re: Your inquiry`}
                    className="px-4 py-2 rounded-lg bg-luna-secondary text-luna-primary text-sm font-medium hover:opacity-90 transition-opacity"
                  >
                    Reply via Email
                  </a>
                </div>
              </div>
            </div>
          )}
          
          <AdminConfirmDialog
            isOpen={showDeleteDialog}
            title={deleteTarget === 'bulk' ? 'Delete Multiple Contacts' : 'Delete Contact'}
            message={
              deleteTarget === 'bulk'
                ? `Are you sure you want to delete ${selectedRows.length} contact(s)? This action cannot be undone.`
                : 'Are you sure you want to delete this contact? This action cannot be undone.'
            }
            confirmText="Delete"
            cancelText="Cancel"
            onConfirm={confirmDelete}
            onCancel={() => {
              setShowDeleteDialog(false);
              setDeleteTarget(null);
            }}
            variant="danger"
          />
        </div>
      );
    };